/**
 * Bodi referral waitlist — ref capture, dual-submit, share panel.
 *
 * Forms opt in via a `[data-bodi-referral]` wrapper div rendered by the
 * snippet bodi-email-form.liquid (referral: true), which carries
 * data-endpoint / data-source and contains the panel <template>.
 * Submit flow: POST to the Supabase waitlist function first (referral
 * tracking, 2s timeout), then let the native {% form 'customer' %} submit
 * proceed so Shopify still creates the customer and Klaviyo still syncs.
 * If the tracking call fails, the native signup always goes ahead.
 *
 * The share panel's markup lives in a <template data-bodi-panel> inside the
 * snippet (so Tailwind scans its classes); this file only clones + hydrates.
 */
(function () {
  'use strict';

  var ENDPOINT = 'https://uhwdblfleoupaekiciun.supabase.co/functions/v1/waitlist';
  var LS_REF = 'bodi_ref'; // captured ?ref= code {code, ts}
  var LS_WL = 'bodi_wl'; //   own signup {code, share_url, count, fresh, ts}
  var LS_PEND = 'bodi_pend'; // submission awaiting confirmation {email, first_name, ref, source, ts}
  var LS_UTM = 'bodi_utm'; //   first paid/tagged landing {utm_*, fbclid, landing_path, referrer, ts}
  var REF_TTL = 30 * 864e5; // 30 days
  var PEND_TTL = 864e5; //    24 hours
  var POST_TIMEOUT = 3500; // ms — must cover an edge-function cold start
  var TIERS = [
    [1, 'the VIP list'],
    [3, 'a free refill'],
    [10, 'a case + 3 refills'],
    [25, '2 cases + 12 refills'],
  ];

  // Capture UTM / click-id attribution from the landing URL. Kept for 30 days
  // (same TTL as a referral code). A tagged landing always overwrites, so the
  // most recent ad click wins; an untagged visit never clears an earlier one.
  function captureUtm() {
    try {
      var q = new URLSearchParams(location.search);
      var keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid', 'gbraid', 'wbraid'];
      var hit = false;
      var rec = {};
      for (var i = 0; i < keys.length; i++) {
        var v = q.get(keys[i]);
        if (v) { rec[keys[i]] = String(v).slice(0, 200); hit = true; }
      }
      // Untagged visit: keep the first landing page and referrer so organic
      // and direct signups aren't blank, but never overwrite an ad click.
      if (!hit && read(LS_UTM)) return;
      rec.landing_path = (location.pathname || '/').slice(0, 200);
      var refHost = '';
      try { refHost = document.referrer ? new URL(document.referrer).hostname : ''; } catch (e2) {}
      rec.referrer = refHost.slice(0, 200);
      rec.ts = Date.now();
      write(LS_UTM, rec);
    } catch (err) { /* attribution is best-effort */ }
  }

  function cookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]).slice(0, 200) : null;
    } catch (e) { return null; }
  }

  function leadEventId() {
    var id = 'bodi-lead-';
    try { id += (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + '-' + Math.random().toString(16).slice(2)); }
    catch (e) { id += String(Date.now()); }
    return id;
  }

  function attribution() {
    var u = read(LS_UTM);
    if (!u) return null;
    var out = {};
    for (var k in u) if (k !== 'ts' && Object.prototype.hasOwnProperty.call(u, k)) out[k] = u[k];
    return out;
  }

  function metaCookies(a) {
    a = a || {};
    var fbp = cookie('_fbp'); var fbc = cookie('_fbc');
    if (fbp) a.fbp = fbp;
    if (fbc) a.fbc = fbc;
    return Object.keys(a).length ? a : null;
  }

  function read(key) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return null;
      var val = JSON.parse(raw);
      if (val.ts && Date.now() - val.ts > REF_TTL) {
        localStorage.removeItem(key);
        return null;
      }
      return val;
    } catch (e) {
      return null;
    }
  }

  function write(key, val) {
    try {
      localStorage.setItem(key, JSON.stringify(val));
    } catch (e) {
      /* private mode — referral tracking degrades, signup unaffected */
    }
  }

  // --- 1. Capture ?ref= on every page load (last click wins) ---------------
  var refParam = new URLSearchParams(location.search).get('ref');
  if (refParam && /^[a-z0-9]{4,8}$/i.test(refParam)) {
    write(LS_REF, { code: refParam.toUpperCase(), ts: Date.now() });
  }

  // --- Panel ---------------------------------------------------------------
  function nextTier(count) {
    for (var i = 0; i < TIERS.length; i++) {
      if (count < TIERS[i][0]) return TIERS[i];
    }
    return null;
  }

  function progressCopy(count) {
    var next = nextTier(count);
    if (!next) return count + ' friends in. You unlocked the lot: 2 cases + 12 refills.';
    if (count === 0) return 'Share your link. 1 friend puts you on the VIP list.';
    var gap = next[0] - count;
    return count + (count === 1 ? ' friend in. ' : ' friends in. ') + gap + ' more for ' + next[1] + '.';
  }

  function withUtm(url, channel) {
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'utm_source=' + channel + '&utm_medium=referral&utm_campaign=waitlist';
  }

  function updateProgress(panel, count) {
    var text = panel.querySelector('[data-wl-progress]');
    if (text) text.textContent = progressCopy(count);
    var bar = panel.querySelector('[data-wl-bar]');
    if (bar) {
      var next = nextTier(count);
      bar.style.width = (next ? Math.min(100, Math.round((count / next[0]) * 100)) : 100) + '%';
    }
  }

  // Wire the share controls (link field, copy, WhatsApp/FB, native share)
  // inside any root element. Shared by the sign-up panel and the
  // thank-you page hub.
  function wireShare(root, wl) {
    var shareMsg = 'I just joined the bodi waitlist. Refillable natural deodorant made in NZ. Use my link to lock in the founding offer, a free case plus 35% off refills: ';

    var link = root.querySelector('[data-wl-link]');
    if (link) link.value = wl.share_url;

    var copy = root.querySelector('[data-wl-copy]');
    if (copy) {
      copy.addEventListener('click', function () {
        // Copied links carry a share UTM so friend traffic is
        // distinguishable from ads in analytics.
        var copyUrl = withUtm(wl.share_url, 'friend-share');
        var done = function () {
          var label = copy.textContent;
          copy.textContent = 'Copied!';
          setTimeout(function () { copy.textContent = label; }, 1600);
        };
        if (navigator.clipboard) navigator.clipboard.writeText(copyUrl).then(done, done);
        else {
          if (link) { link.select(); document.execCommand('copy'); }
          done();
        }
      });
    }

    var wa = root.querySelector('[data-wl-wa]');
    if (wa) wa.href = 'https://wa.me/?text=' + encodeURIComponent(shareMsg + withUtm(wl.share_url, 'whatsapp'));

    var fb = root.querySelector('[data-wl-fb]');
    if (fb) fb.href = 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(withUtm(wl.share_url, 'facebook'));

    var native = root.querySelector('[data-wl-native]');
    if (native) {
      if (navigator.share) {
        native.addEventListener('click', function () {
          navigator.share({ title: 'bodi', text: shareMsg, url: withUtm(wl.share_url, 'native') }).catch(function () {});
        });
      } else {
        native.hidden = true;
      }
    }
  }

  // Re-fetch the live count for a code, then run cb(count).
  function refreshCount(endpoint, wl, cb) {
    if (!endpoint) return;
    fetch(endpoint + '?code=' + encodeURIComponent(wl.code))
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data && data.ok) {
          wl.count = data.referral_count;
          write(LS_WL, wl);
          cb(data);
        }
      })
      .catch(function () {});
  }

  function renderPanel(wrap, form, wl) {
    var tpl = wrap.querySelector('template[data-bodi-panel]');
    if (!tpl) return;
    var panel = tpl.content.firstElementChild.cloneNode(true);
    wireShare(panel, wl);
    updateProgress(panel, wl.count || 0);
    updateTiers(panel, wl.count || 0);
    form.hidden = true;
    form.parentNode.insertBefore(panel, form.nextSibling);

    // Split-hero joined state: hide the founding-offer chrome and widen the
    // column so the hub is the only box, at half the hero's width.
    var offerBox = wrap.closest('.bodi-offerbox');
    if (offerBox) offerBox.classList.add('bodi-offerbox--joined');
    var split = wrap.closest('.bodi-split');
    if (split) split.classList.add('bodi-split--joined');
    refreshCount(wrap.getAttribute('data-endpoint'), wl, function () {
      updateProgress(panel, wl.count);
      updateTiers(panel, wl.count);
    });
  }

  // --- Thank-you page (/pages/thank-you) --------------------------------
  function updateTiers(root, count) {
    var next = nextTier(count);
    root.querySelectorAll('[data-ty-tier]').forEach(function (tier) {
      var n = parseInt(tier.getAttribute('data-ty-tier'), 10);
      var state = tier.querySelector('[data-ty-state]');
      var togo = tier.querySelector('[data-ty-togo]');
      // Opt-in: rungs carrying data-ty-worth show their value while locked and
      // put the countdown in [data-ty-togo]. Anything without it (the sign-up
      // panel's mini tiers) keeps the original Locked / N to go wording.
      var worth = tier.getAttribute('data-ty-worth');
      var hasWorth = worth !== null;
      tier.classList.remove('is-unlocked', 'is-next');
      if (togo) togo.textContent = '';
      if (count >= n) {
        tier.classList.add('is-unlocked');
        if (state) state.textContent = 'Unlocked';
      } else if (next && n === next[0]) {
        tier.classList.add('is-next');
        var left = n - count;
        if (state) state.textContent = hasWorth ? worth : left + ' to go';
        if (togo) togo.textContent = left + (left === 1 ? ' more friend' : ' more friends');
      } else {
        if (state) state.textContent = hasWorth ? worth : 'Locked';
      }
    });
    updateRail(root, count);
  }

  // Fill the rail's progress line to the visitor's actual position: past the
  // last unlocked node, then proportionally on toward the next one. Uses
  // offsetTop (layout-relative) rather than getBoundingClientRect, so the
  // scroll-reveal wrapper's transform can't skew the measurement.
  function updateRail(root, count) {
    var rail = root.querySelector('.ty-rail');
    if (!rail) return;
    var rungs = Array.prototype.slice.call(rail.querySelectorAll('[data-ty-tier]'));
    if (!rungs.length) return;
    var tierOf = function (r) { return parseInt(r.getAttribute('data-ty-tier'), 10); };

    var done = null, next = null;
    rungs.forEach(function (r) {
      if (count >= tierOf(r)) done = r;
      else if (!next) next = r;
    });

    var px = 0;
    if (done && next) {
      var a = tierOf(done), b = tierOf(next);
      var frac = b > a ? (count - a) / (b - a) : 0;
      px = done.offsetTop + frac * (next.offsetTop - done.offsetTop);
    } else if (done) {
      px = done.offsetTop;                                   // every tier unlocked
    } else if (next) {
      px = (count / tierOf(next)) * next.offsetTop;          // on the way to the first
    }
    rail.style.setProperty('--ty-progress', Math.max(0, Math.round(px)) + 'px');
  }

  // The running count of friends who joined from this link. Zero gets a
  // prompt rather than a flat "0 friends".
  function updateCount(root, count) {
    var num = root.querySelector('[data-wl-count]');
    var label = root.querySelector('[data-wl-count-label]');
    if (!num && !label) return;
    var n = count || 0;
    if (num) num.textContent = n;
    if (label) {
      label.textContent = n === 0 ? 'No one yet. Your move.'
        : n === 1 ? 'friend has signed up from your link'
        : 'friends have signed up from your link';
    }
  }

  function hydrateThanks(el) {
    updateTiers(el, 0); // sensible default while we resolve the visitor
    updateCount(el, 0);

    var endpoint = el.getAttribute('data-endpoint');
    var finish = function (wl) {
      if (wl.fresh) {
        // Join recovered on this page — still counts as the Lead moment.
        if (typeof window.fbq === 'function') window.fbq('track', 'Lead');
        wl.fresh = 0;
        write(LS_WL, wl);
      }
      wireShare(el, wl);
      updateProgress(el, wl.count || 0);
      updateCount(el, wl.count || 0);
      updateTiers(el, wl.count || 0);
      refreshCount(endpoint, wl, function (data) {
        updateProgress(el, wl.count);
        updateCount(el, wl.count);
        updateTiers(el, wl.count);
        var nm = el.querySelector('[data-ty-name]');
        if (nm && data.first_name) nm.textContent = ', ' + data.first_name;
      });
    };

    var wl = read(LS_WL);
    if (wl) return finish(wl);

    // Timed-out submit? Recover it (idempotent), else fall back to anon.
    var pend = read(LS_PEND);
    if (pend && Date.now() - pend.ts < PEND_TTL && pend.email) {
      postJoin(endpoint, pend, 10000)
        .then(function (recovered) {
          if (recovered) finish(recovered);
          else el.classList.add('ty-anon');
        })
        .catch(function () { el.classList.add('ty-anon'); });
    } else {
      el.classList.add('ty-anon');
    }
  }

  // Tie this browser to the profile in klaviyo.js. Klaviyo's "How they found
  // you" panel (source, medium, campaign, first page, referrer) is filled only
  // when onsite tracking identifies a visitor; the Shopify form and the
  // waitlist function both create the profile server-side, so without this
  // the panel stays blank. Klaviyo's own popups identify themselves already.
  function klaviyoIdentify(payload) {
    try {
      if (!window.klaviyo || !payload.email) return;
      var props = { email: payload.email };
      if (payload.first_name) props.first_name = payload.first_name;
      window.klaviyo.identify(props);
    } catch (err) { /* tracking is best-effort; never block the submit */ }
  }

  // POST a join to the waitlist function; stores bodi_wl + clears the pending
  // stash on a definitive answer. Resolves with the wl record or null.
  function postJoin(endpoint, payload, timeoutMs) {
    var ctrl = 'AbortController' in window ? new AbortController() : null;
    var timer = ctrl && setTimeout(function () { ctrl.abort(); }, timeoutMs);
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      signal: ctrl ? ctrl.signal : undefined,
      body: JSON.stringify({
        email: payload.email,
        first_name: payload.first_name,
        ref: payload.ref,
        source: payload.source,
        attribution: metaCookies(payload.attribution || attribution()),
        lead_event_id: payload.lead_event_id || null,
      }),
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (timer) clearTimeout(timer);
        if (!data) return null;
        localStorage.removeItem(LS_PEND); // definitive answer either way
        if (!data.ok) return null;
        var wl = {
          code: data.code,
          share_url: data.share_url,
          count: data.referral_count,
          fresh: data.existing ? 0 : 1,
          ts: Date.now(),
        };
        write(LS_WL, wl);
        return wl;
      });
  }

  // --- 2. Dual-submit interception ----------------------------------------
  function bind(wrap, form) {
    form.addEventListener('submit', function (e) {
      if (form.dataset.bodiSent) return; // safety: never intercept twice
      e.preventDefault();
      var btn = form.querySelector('[type="submit"]');
      if (btn) btn.disabled = true;

      var proceed = function () {
        form.dataset.bodiSent = '1';
        if (btn) btn.disabled = false;
        form.submit(); // native Shopify customer capture — always happens
      };

      try {
        var emailInput = form.querySelector('input[name="contact[email]"]');
        var nameInput = form.querySelector('input[name="contact[first_name]"]');
        var ref = read(LS_REF);
        var payload = {
          email: emailInput ? emailInput.value : '',
          first_name: nameInput ? nameInput.value : '',
          ref: ref ? ref.code : null,
          source: wrap.getAttribute('data-source') || 'waitlist',
          attribution: attribution(),
          lead_event_id: leadEventId(),
        };
        // Shared event id: the custom pixel (Settings > Customer events) reads this
        // hidden field for its eid, so Meta dedupes browser + server Lead.
        var hid = form.querySelector('input[name="lead_event_id"]');
        if (!hid) { hid = document.createElement('input'); hid.type = 'hidden'; hid.name = 'lead_event_id'; form.appendChild(hid); }
        hid.value = payload.lead_event_id;
        // Stash before the network call: if the POST times out mid-navigation,
        // the next page load re-submits it (idempotent) and recovers the code.
        payload.ts = Date.now();
        write(LS_PEND, payload);
        klaviyoIdentify(payload);

        postJoin(wrap.getAttribute('data-endpoint'), payload, POST_TIMEOUT).then(proceed, proceed);
      } catch (err) {
        proceed();
      }
    });
  }

  // --- 3. Init -------------------------------------------------------------
  function activate(wraps, wl) {
    if (wl.fresh) {
      // First page view after a successful join — the Meta optimisation event.
      if (typeof window.fbq === 'function') window.fbq('track', 'Lead');
      wl.fresh = 0;
      write(LS_WL, wl);
    }
    wraps.forEach(function (wrap) {
      var form = wrap.querySelector('form');
      if (form && !wrap.querySelector('[data-wl-link]')) renderPanel(wrap, form, wl);
    });
  }

  function init() {
    captureUtm();
    var wraps = document.querySelectorAll('[data-bodi-referral]');
    var thanks = document.querySelector('[data-bodi-thanks]');
    if (!wraps.length && !thanks) return;
    var wl = read(LS_WL);

    if (wl && wl.fresh) {
      // First page view after a successful join — the Meta optimisation event.
      if (typeof window.fbq === 'function') window.fbq('track', 'Lead');
      wl.fresh = 0;
      write(LS_WL, wl);
    }

    if (thanks) hydrateThanks(thanks); // handles its own pend recovery

    if (!wraps.length) return;
    if (wl) return activate(wraps, wl); // joined already → panel, not form

    wraps.forEach(function (wrap) {
      var form = wrap.querySelector('form');
      if (form) bind(wrap, form);
    });

    // Arrived on a friend's link and not yet joined: confirm the invite is
    // live so the "we both move up" promise from the share message holds.
    var refIn = read(LS_REF);
    if (refIn && !thanks) {
      wraps.forEach(function (wrap) {
        if (!wrap.querySelector('form')) return;
        var note = document.createElement('div');
        note.className = 'mb-3 text-center';
        note.innerHTML = '<span class="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold font-body bg-[#FAD11B] text-[#144634]">Your friend’s invite is in. Join free for the founding offer, and get your own gift link.</span>';
        wrap.insertBefore(note, wrap.firstChild);
      });
    }

    // A submit whose tracking call never answered (timeout mid-navigation):
    // re-POST it — the endpoint is idempotent, so this recovers the code or
    // completes the join, and the share panel appears as if nothing happened.
    if (thanks) return; // the thank-you hub already ran recovery
    var pend = read(LS_PEND);
    if (pend && Date.now() - pend.ts < PEND_TTL && pend.email) {
      postJoin(wraps[0].getAttribute('data-endpoint'), pend, 10000)
        .then(function (recovered) {
          if (recovered) activate(wraps, recovered);
        })
        .catch(function () {});
    }
  }

  // --- Klaviyo onsite forms (popups/flyouts) -----------------------------
  // Klaviyo fires a `klaviyoForms` event on submit with the email. Feed it
  // into the referral system so popup signups get a code, keep any friend's
  // referral credit, and land personalised on /pages/thank-you. The pend
  // stash means even a submit that redirects mid-flight recovers there.
  window.addEventListener('klaviyoForms', function (e) {
    try {
      var d = e.detail || {};
      if (d.type !== 'submit') return;
      if (read(LS_WL)) return; // already joined
      var meta = d.metaData || {};
      var email = String(meta.$email || meta.email || '').trim();
      if (!email) return;
      var ref = read(LS_REF);
      var payload = {
        email: email,
        first_name: String(meta.$first_name || meta.first_name || '').trim(),
        ref: ref ? ref.code : null,
        source: 'popup',
        attribution: attribution(),
        lead_event_id: leadEventId(),
        ts: Date.now(),
      };
      write(LS_PEND, payload);
      postJoin(ENDPOINT, payload, 10000).catch(function () {});
    } catch (err) {
      /* popup capture is best-effort; the Klaviyo submit itself is unaffected */
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
